import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SelectedFolder } from "./useFolderChildren";
import { DocumentName, DocumentNameKind } from "client/Features/Documents/DocumentName";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	Icon,
	ListItem,
	TableView,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	TableViewNoResultConfig
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { isNotNilOrEmpty } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	MediaQueryKind,
	createLocalDataSource,
	useDeepComparison,
	useSortHandler,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import { contains } from "client/Data/searching";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";

export type FolderListProps = {
	searchTerm: string;
	currentFolder: SelectedFolder | undefined;
	items: readonly ObjectWithClientId<SelectedFolder>[];
	onOpen(item: SelectedFolder): void;
};

export const FolderList = (props: FolderListProps): ReactElement => {
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	return isMobile
		? <MobileFolderList {...props} />
		: <DesktopFolderList {...props} />;
};
FolderList.displayName = "FolderList";

const DesktopFolderList = (props: FolderListProps): ReactElement => {
	const {
		searchTerm,
		items,
		onOpen,
		currentFolder
	} = props;

	const [sortingState, setSortingState] = useState<DataSourceSortableState<SelectedFolder>>(defaultSorting);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	const {
		currentItems
	} = useDataSource(
		items,
		{
			sorting: sortingState,
			searchTerm
		}
	);

	const listVersion: number = useDeepComparison([
		searchTerm,
		sortingState,
		currentFolder?.id
	]);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(listVersion);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[listVersion]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "folder-open",
				title: localize("_NoItemsToShow"),
				message: localize("_ThereAreCurrentlyNoItemsToShowHere")
			};
		},
		[searchTerm]
	);

	return (
		<TableView
			columns={[
				{
					title: localize("_Name"),
					trackingId: "name",
					width: "fill",
					sortable: sortingFor("name"),
					maxWidth: Number.MAX_SAFE_INTEGER
				}
			]}
			items={currentItems}
			itemKey={getItemKey}
			trackingId="folder-list"
			isVirtual
			ref={tableRef}
			shouldEvaluateBackgroundColor
			height="stretch"
			noResultConfig={noResultConfig}
		>
			{(x, itemId) => (
				<TableView.Row
					trackingId={itemId.toString()}
					onExecute={() => onOpen(x)}
				>
					<TableView.Cell shouldExecuteOnClick>
						<DocumentName
							name={x.name}
							documentKind={DocumentItemType.Folder}
							mode={{ kind: DocumentNameKind.Default }}
						/>
					</TableView.Cell>
				</TableView.Row>
			)}
		</TableView>
	);
};
DesktopFolderList.displayName = "DesktopFolderList";

const MobileFolderList = (props: FolderListProps): ReactElement => {
	const {
		items,
		onOpen,
		searchTerm,
		currentFolder
	} = props;

	const virtualListRef: RefObject<ScrollableVirtualListHandle<ObjectWithClientId<SelectedFolder>> | null> = useRef<ScrollableVirtualListHandle<ObjectWithClientId<SelectedFolder>>>(null);

	const {
		currentItems
	} = useDataSource(
		items,
		{
			searchTerm,
			sorting: defaultSorting
		}
	);

	const listVersion: number = useDeepComparison([
		searchTerm,
		currentFolder?.id
	]);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(listVersion);

			virtualListRef.current
				?.scrollToTop();
		},
		[listVersion]
	);

	return (
		<ScrollableVirtualList
			items={currentItems}
			itemKey={getItemKey}
			estimatedHeightOfAnItem={64}
			ref={virtualListRef}
			trackingId="folder-list"
		>
			{(folder: ObjectWithClientId<SelectedFolder>): ReactElement => (
				<ListItem trackingId={getItemKey(folder)}>
					<ListItem.Content
						onClick={() => onOpen(folder)}
						thumbnail={
							<Icon
								value={{ kind: "svg", name: "folder" }}
								size="large"
							/>
						}
					>
						<ListItem.Title>{folder.name}</ListItem.Title>
					</ListItem.Content>
				</ListItem>
			)}
		</ScrollableVirtualList>
	);
};
MobileFolderList.displayName = "MobileFolderList";

function getItemKey(item: ObjectWithClientId<SelectedFolder>): string {
	return isNotNilOrEmpty(item.id)
		? item.id
		: getClientId(item);
}

const useDataSource = createLocalDataSource<ObjectWithClientId<SelectedFolder>>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm)
		}
	})
	.getHook();

const defaultSorting: DataSourceSortableState<SelectedFolder> = {
	direction: DataSourceSortDirection.Ascending,
	propertyName: "name"
};